import { Search2Icon } from "@chakra-ui/icons";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  Stack,
  Text,
  useBreakpointValue,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type PortalMyBidDto,
  PublicBidStatus,
  type PortalSourcingEventSummaryDto,
  useGetPortalMyBidsQuery,
  useGetPortalSourcingEventsQuery,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useAppSelector } from "../../../../common/redux/hooks";
import { PortalLayout } from "../components/PortalLayout";
import { PortalSourcingEventCard } from "../components/PortalSourcingEventCard";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

type BidFilter = PublicBidStatus | "ALL";

// Chip order + i18n label key per bid status (labels shared with PublicBidStatusTag).
const BID_STATUS_ORDER: PublicBidStatus[] = [
  PublicBidStatus.Draft,
  PublicBidStatus.Submitted,
  PublicBidStatus.Awarded,
  PublicBidStatus.Rejected,
];
const BID_STATUS_LABEL_KEY: Record<PublicBidStatus, string> = {
  [PublicBidStatus.Draft]: "Draft",
  [PublicBidStatus.Submitted]: "Submitted",
  [PublicBidStatus.Awarded]: "Awarded",
  [PublicBidStatus.Rejected]: "Rejected",
};

const matchesQuery = (bid: PortalMyBidDto, query: string): boolean => {
  if (!query) return true;
  const haystack = [bid.event.title, bid.event.buyerName, bid.event.referenceId ?? ""].join(" ").toLowerCase();
  return haystack.includes(query.toLowerCase());
};

const countByStatus = (bids: PortalMyBidDto[]): Map<PublicBidStatus, number> => {
  const counts = new Map<PublicBidStatus, number>();
  for (const bid of bids) counts.set(bid.status, (counts.get(bid.status) ?? 0) + 1);
  return counts;
};

export type PortalEventsVariant = "combined" | "browse";

export const PortalSourcingEventsPage = ({ variant = "combined" }: { variant?: PortalEventsVariant }) => {
  const { t } = useTranslation();
  const { triggerSoft, show } = useApiError();
  const searchPlaceholder = useBreakpointValue({
    base: t("Search procurements"),
    md: t("Search by title, buyer, reference ID or CPV code..."),
  });
  const [cursor, setCursor] = useState<string | undefined>();
  const [events, setEvents] = useState<PortalSourcingEventSummaryDto[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [totalCount, setTotalCount] = useState<number | undefined>();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [bidFilter, setBidFilter] = useState<BidFilter>("ALL");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCursor(undefined);
    setEvents([]);
    setNextCursor(undefined);
    setTotalCount(undefined);
  }, [debouncedQuery]);

  const { currentData, error, isLoading, isFetching } = useGetPortalSourcingEventsQuery(
    {
      cursor,
      limit: PAGE_SIZE,
      q: debouncedQuery || undefined,
    },
    { refetchOnFocus: true, skip: bidFilter !== "ALL" }
  );

  useEffect(() => {
    if (!currentData) return;

    setEvents((previousEvents) => mergeUniqueById(cursor ? [...previousEvents, ...currentData.events] : currentData.events));
    setNextCursor(currentData.nextCursor ?? undefined);
    setTotalCount(currentData.totalCount);
  }, [currentData, cursor]);

  useEffect(() => {
    if (error && events.length > 0) {
      triggerSoft(error);
    }
  }, [error, events.length, triggerSoft]);

  // The supplier's own bid status per event, merged client-side so the anonymous listing stays
  // anonymous (the my-bids endpoint is authenticated and org-scoped).
  const authState = useAppSelector((state) => state.authState.state);
  const isLoggedInWithOrg = authState?.type === "LoggedInWithOrgContext";
  const { data: myBids } = useGetPortalMyBidsQuery({}, { skip: !isLoggedInWithOrg });
  const myBidsList = useMemo(() => myBids?.bids ?? [], [myBids]);
  const bidStatusByEvent = useMemo(
    () => new Map(myBidsList.map((bid) => [bid.eventId, bid.status] as const)),
    [myBidsList]
  );
  const searchedMyBids = useMemo(
    () => myBidsList.filter((bid) => matchesQuery(bid, debouncedQuery)),
    [myBidsList, debouncedQuery]
  );
  // Total per status decides which chips exist (stable across search); the visible count on each
  // chip reflects the active search so it matches the number of rendered cards.
  const bidTotalByStatus = useMemo(() => countByStatus(myBidsList), [myBidsList]);
  const bidVisibleByStatus = useMemo(() => countByStatus(searchedMyBids), [searchedMyBids]);

  // A status filter renders the supplier's own bids (a complete, unpaginated set) rather than the
  // paginated public list, so it stays correct regardless of how far the public list is scrolled.
  const filteredMyBids = useMemo(
    () => (bidFilter === "ALL" ? [] : searchedMyBids.filter((bid) => bid.status === bidFilter)),
    [bidFilter, searchedMyBids]
  );

  // Never leave the page stuck on a status filter that has no bids (e.g. the my-bids set emptied
  // after an org switch) — fall back to the full public listing so "All" is always reachable.
  useEffect(() => {
    if (bidFilter !== "ALL" && !bidTotalByStatus.get(bidFilter)) setBidFilter("ALL");
  }, [bidFilter, bidTotalByStatus]);

  const isSearchActive = debouncedQuery !== "";
  const showInitialSkeleton = isLoading && events.length === 0;
  const showEmptyState = !showInitialSkeleton && events.length === 0;

  const showChips = variant !== "browse" && isLoggedInWithOrg && myBidsList.length > 0;

  return (
    <PortalLayout>
      {error && events.length === 0 ? (
        show(error)
      ) : (
        <>
          <Flex align={{ base: "flex-start", md: "center" }} justify="space-between" gap="3" wrap="wrap">
            <HStack spacing="3" align="center" flexWrap="wrap">
              <Heading as="h1" size="lg">
                {t("Active procurements")}
              </Heading>
              {totalCount !== undefined && (
                <Badge bg="smPrimarySofter" color="smPrimary" px="3" py="1" rounded="full" fontSize="sm" textTransform="none">
                  {t("procurement_count", { count: totalCount })}
                </Badge>
              )}
            </HStack>
          </Flex>

          <Text color="smMuted" mt="2">
            {t("Find and participate in public procurements")}
          </Text>

          <InputGroup size="lg" mt="6" mb="5">
            <InputLeftElement pointerEvents="none" h="full" display="flex" alignItems="center" justifyContent="center">
              <Search2Icon color="smTextDisabled" boxSize="4" />
            </InputLeftElement>
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={searchPlaceholder ?? ""}
              bg="smBackground"
              borderColor="smBorder"
              rounded="xl"
              focusBorderColor="smPrimary"
              _focusVisible={{ borderColor: "smPrimary", boxShadow: "0 0 0 1px var(--chakra-colors-smPrimary)" }}
              _hover={{ borderColor: "smPrimarySoft" }}
              _placeholder={{ color: "smTextDisabled" }}
            />
          </InputGroup>

          {showChips && (
            <Wrap spacing="2" mb="5">
              <WrapItem>
                <FilterChip active={bidFilter === "ALL"} label={t("All")} onClick={() => setBidFilter("ALL")} />
              </WrapItem>
              {BID_STATUS_ORDER.filter((status) => bidTotalByStatus.get(status)).map((status) => (
                <WrapItem key={status}>
                  <FilterChip
                    active={bidFilter === status}
                    label={`${t(BID_STATUS_LABEL_KEY[status])} (${bidVisibleByStatus.get(status) ?? 0})`}
                    onClick={() => setBidFilter(status)}
                  />
                </WrapItem>
              ))}
            </Wrap>
          )}

          {bidFilter !== "ALL" ? (
            filteredMyBids.length === 0 ? (
              <Card variant="outline" rounded="xl">
                <CardBody py="20">
                  <Flex align="center" justify="center">
                    <Text color="smMuted">{t("No procurements match your current search")}</Text>
                  </Flex>
                </CardBody>
              </Card>
            ) : (
              <Stack spacing="4">
                {filteredMyBids.map((bid) => (
                  <PortalSourcingEventCard key={bid.event.id} event={bid.event} bidStatus={bid.status} />
                ))}
              </Stack>
            )
          ) : showInitialSkeleton ? (
            <Stack spacing="4">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} height="180px" rounded="xl" startColor="smBackground" endColor="smBackgroundTertiary" />
              ))}
            </Stack>
          ) : showEmptyState ? (
            <Card variant="outline" rounded="xl">
              <CardBody py="20">
                <Flex align="center" justify="center">
                  <Text color="smMuted">
                    {isSearchActive ? t("No procurements match your current search") : t("No public procurements are available right now")}
                  </Text>
                </Flex>
              </CardBody>
            </Card>
          ) : (
            <Stack spacing="4">
              {events.map((event) => (
                <PortalSourcingEventCard key={event.id} event={event} bidStatus={bidStatusByEvent.get(event.id)} />
              ))}
            </Stack>
          )}

          {bidFilter === "ALL" && nextCursor && (
            <Flex justify="center" pt="8">
              <Button variant="link" color="smPrimary" isLoading={isFetching} onClick={() => setCursor(nextCursor)}>
                {t("Load more")}
              </Button>
            </Flex>
          )}
        </>
      )}
    </PortalLayout>
  );
};

const FilterChip = ({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) => (
  <Button
    size="sm"
    borderRadius="full"
    variant={active ? "solid" : "outline"}
    colorScheme={active ? "teal" : "gray"}
    onClick={onClick}
    aria-pressed={active}
  >
    {label}
  </Button>
);

const mergeUniqueById = (events: PortalSourcingEventSummaryDto[]) => {
  const eventsById = new Map(events.map((event) => [event.id, event]));
  return Array.from(eventsById.values());
};
